import type { NotificationSubscriptionDto } from "@keydials/api-client";
import { HELP_ANCHORS } from "@keydials/ui";
import type { DialVM } from "../../dialModel";
import { simulationLabel } from "../../workspace/checkingRole";
import { SubscribeButton } from "../../workspace/SubscribeButton";

/**
 * The dial's top bar: what dial you are looking at, and whether you are subscribed to it.
 *
 * It used to carry a row of buttons too — Status roles, Feeds, Settings — each opening a modal or a page.
 * They are all one place now, the overview's Settings tab, which is why nothing here changes the dial any
 * more. Only Subscribe remains, and that is a personal choice rather than a dial setting.
 */
export function DialBar({
  vm,
  org,
  definitionName,
  subs,
  onSubsChanged,
  meHasPhoneNumber,
  onClearSimulatedRole,
  groupName,
}: {
  vm: DialVM;
  org?: string | null;
  /** The source definition's name, shown greyed after the instance name (what kind of dial this is). */
  definitionName?: string | null;
  subs: NotificationSubscriptionDto[];
  onSubsChanged: () => void | Promise<void>;
  /** Whether the current user has a phone number on file — gates the SMS channel (KEY-453). */
  meHasPhoneNumber?: boolean;
  /** KEY-498: clears the checking-role simulation, for the chip's one-click way back. */
  onClearSimulatedRole?: () => void;
  /** KEY-606: the group dial this one was opened from, named on its own line above the dial's name. */
  groupName?: string | null;
}) {
  // KEY-498. Always visible while it is in force, and one click from being off: a forgotten impersonation
  // reads as a broken permission — "why can't I set this status any more?" — and the answer has to be on
  // screen, not two scrolls up in the banner.
  const simulation = simulationLabel(vm.detail.simulatedRole);
  return (
    // KEY-526: its own band, not a continuation of the app header. --surface was literally the same value
    // as --header-bg in the light theme, so the two strips merged and the dial name merged with them.
    // No top border: the header already draws its own bottom one, and a second line here just reads as a
    // 2px smudge. The tonal step is what separates the two bars.
    <div style={{ background: "var(--dialbar-bg)", borderBottom: "var(--border-w) solid var(--dialbar-border)" }}>
      <div
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          padding: "12px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        {/* KEY-606: inside a group, the title reads group / dial / organisation, one line each — working through a
            group of schools, "which group, which dial, whose" is the question every time you land on a child. */}
        {groupName && groupName !== vm.detail.name && (
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-faint)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {groupName}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          {/* KEY-526: the DIAL leads, with the organisation greyed to its right — the other way round until
              now. This is the one place the open dial is named, and it was the muted middle item behind the
              org; worse, it carried kd-hide-sm, so on a narrow screen the dial name was not rendered at all
              and the bar named only the organisation. */}
          <div className="display" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: "var(--text)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {vm.detail.name}
          </div>
          {/* Rides in the chrome so it stays visible as you scroll past the banner below (KEY-283). Gated on
              the SUBTREE (KEY-524) so a child of a group under test is badged too — it is just as throwaway. */}
          {(vm.detail.inTestingSubtree ?? vm.detail.isTesting) && (
            <span
              style={{
                fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
                color: "#fff", background: "var(--st-risk, #7a3b9c)", borderRadius: 999, padding: "2px 8px",
                flex: "none",
              }}
              title="This dial is being tested. Anything entered into it is deleted when testing finishes."
            >
              Testing
            </span>
          )}
          {/* KEY-938: the same treatment for a demo dial, so the affiliate can tell their two copies apart. */}
          {vm.detail.isDemo && (
            <span
              style={{
                fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
                color: "#fff", background: "var(--st-info, #2563eb)", borderRadius: 999, padding: "2px 8px",
                flex: "none",
              }}
              title="This is a demo dial. Files are never stored in it, and everything in it is made up."
            >
              Demo
            </span>
          )}
          {simulation && (
            <span
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, flex: "none",
                fontSize: 11.5, fontWeight: 700, letterSpacing: ".02em",
                color: "var(--accent-text)", background: "var(--accent-soft)",
                border: "var(--border-w) solid var(--accent-border)",
                borderRadius: 999, padding: "2px 4px 2px 9px",
              }}
              title="You are checking this dial as another role. Only what that role may DO is changed — you still see the whole dial."
            >
              {simulation}
              {onClearSimulatedRole && (
                <button
                  type="button"
                  onClick={onClearSimulatedRole}
                  aria-label="Back to my own role"
                  title="Back to my own role"
                  style={{
                    border: "none", background: "transparent", color: "inherit", cursor: "pointer",
                    font: "inherit", lineHeight: 1, padding: "2px 6px", borderRadius: 999,
                  }}
                >
                  ✕
                </button>
              )}
            </span>
          )}
          {definitionName && definitionName !== vm.detail.name && (
            <span style={{ fontSize: 13, color: "var(--text-faint)" }} className="kd-hide-sm">
              · {definitionName}
            </span>
          )}
        </div>
        {/* KEY-606: WHOSE dial this is, on its own line so it is never squeezed out. Working through a group of
            seventy schools the question is "which school am I in?" — this used to be a small grey suffix that
            vanished below 560px. Skipped when the dial is already named for the organisation. */}
        {org && org !== vm.detail.name && (
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-muted)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {org}
          </div>
        )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <SubscribeButton
              instanceId={vm.detail.id}
              scope={{ sectionKey: null, segmentKey: null }}
              subs={subs}
              onChanged={onSubsChanged}
              scopeLabel="this dial"
              meHasPhoneNumber={meHasPhoneNumber}
              helpAnchorId={HELP_ANCHORS.dialSubscribe}
            />
          </div>
        </div>
      </div>


    </div>
  );
}
